import { Fragment } from 'react';
import type { ReportContent, ReportContentRow, ReportContentSection, ReportEvidencePhotoItem, ReportStyle } from '../lib/api/reports.gen';
import { DEFAULT_REPORT_LAYOUT, DEFAULT_SECTION_PRESENTATION } from '../lib/agentShared/formSchema';
// Story 4-128b — INDIVIDUAL pure document components, never the app UI barrel
// (`components/ui/index.ts`, 142 value exports). P06 §4.3 states the rule this
// import block obeys verbatim: "Import individual pure primitives; do not pull
// the entire app UI barrel into the worker bundle." Before this story this file
// imported the barrel, so every screen-only primitive in the app (modals,
// popovers, the agent dock, mapbox-backed surfaces) was a static dependency of
// the report document. These eleven files now live HERE, in the document
// boundary, and `components/ui/index.ts` re-exports them so the design gallery
// and every existing call site are unaffected.
import { ReportScoreStrip } from './components/ReportScoreStrip';
import { ReportTableOfContents } from './components/ReportTableOfContents';
import { ReportFindingsRollup } from './components/ReportFindingsRollup';
import { ReportDisclaimer } from './components/ReportDisclaimer';
import { ReportMediaSummaryGrid } from './components/ReportMediaSummaryGrid';
import type { ReportMediaSummaryPhoto } from './components/ReportMediaSummaryGrid';
import { ReportInlinePhotoGrid } from './components/ReportInlinePhotoGrid';
import { ReportDataTable } from './components/ReportDataTable';
import { ReportCoverPage } from './components/ReportCoverPage';
import { ReportLetterhead } from './components/ReportLetterhead';
import { ReportLogoImage } from './components/ReportLogoImage';
// The two design-system primitives the document legitimately still shares with
// the app (audited, story 4-128b): both are pure presentational leaves — no
// router, no query client, no store, no app CSS import — and re-implementing
// either here would be the one-off component DOD §4 rejects. Imported by FILE,
// not through the barrel. Known, recorded transitive weight for `4-128c` (the
// artifact-build child, where tree-shaking and the bundle manifest are the
// right lever): `Chip.tsx` also declares `SelectableOptionChip`, which pulls
// `Button`/`IconButton`/heroicons into this module graph even though the
// document never renders it.
import { OptionChip } from '../components/ui/Chip';
import { deriveSecondaryTextColor } from './reportColor';
// Story 4-128b — the document brings its OWN paper. Not an app stylesheet: this
// is the boundary's own `document.css`, which imports `fonts.css` and the
// scoped Tailwind compile beside it. Importing it HERE rather than from each
// host page is what makes "mounted without its paper rules" unrepresentable —
// before this story, whether the report got its print rules depended on which
// route you reached it through (`ReportDetailPage`/`SharedReportPage` imported
// `reportPrint.css`; Report Studio's canvas did not).
import './styles/document.css';

// ═══ THE CANONICAL PURE DOCUMENT BOUNDARY (story 4-128b, P06 §4.3) ═════════
// Was `modules/reports/components/ReportRender.tsx` until this story; moved
// here (git-tracked rename, not a copy — P06 §4.3: "a renamed path needs an
// audit mapping, not a second copy") and renamed to the name §4.3's own
// migration table gives it. `modules/reports` re-exports it under BOTH names,
// so every existing consumer (review detail, report detail, the share viewer,
// Report Studio's canvas, the design gallery) compiles and renders unchanged.
//
// ── What this file is allowed to import, audited for this story ────────────
// The literal acceptance bar is "zero imports from app CSS/router/global
// state". Every import above was checked against it:
//   * `react` — Fragment only. No hooks, no context. Pure.
//   * `../lib/api/reports.gen` — TYPE-ONLY (`import type`, erased at build).
//     This is the generated CONTRACT, the render protocol's own input shape,
//     which P06 §4.3 requires the boundary to keep ("validated normalized
//     content"). Not app state.
//   * `../lib/agentShared/formSchema` — two frozen default objects
//     (`DEFAULT_REPORT_LAYOUT`, `DEFAULT_SECTION_PRESENTATION`). Shared,
//     synced, side-effect-free DATA that the backend resolves against too;
//     the one thing that must NOT fork between hosts. Not app state.
//   * `./components/*` — the eleven document components, now co-located here.
//   * `../components/ui/Chip` — one pure presentational leaf, imported by
//     FILE (see that import's own note).
//   * `./reportColor` — pure colour math, moved into the boundary with the
//     tree it serves.
// Deliberately ABSENT, and enforced by `frontend/scripts/check-report-document
// -boundary.mjs` rather than by reading: `react-router-dom`, `@tanstack/react-
// query`, `axios`, any `stores/`/`context/` module, any `styles/*.css` import,
// any `modules/` import, and the `components/ui` BARREL.
//
// ── What is NOT here, and why ──────────────────────────────────────────────
// Paper STYLE is no longer emergent: `./styles/document.css` + `./styles/
// fonts.css` own every paper-affecting rule (story 4-128b), and `./paginate
// .ts` owns every generated `@page`/running-element rule plus the single
// `LETTERHEAD_RESERVE_MM`. This file contributes markup and class NAMES only.
//
// THE frontend renderer (P06 §4; story 4-78). Consumes 4-76's render-model +
// style contract (`contracts/reports.js` `ReportContent`) and is the ONLY
// place "render a report" is coded in React — the reports module's public
// export, used by BOTH the template editor's live preview
// (`components/formBuilder/ReportDesigner.tsx`, fed a placeholder
// `ReportContent` built from the draft schema) and the review/report-detail
// surfaces (fed the real one from the API). A template author previewing
// their layout and a reviewer reading a submitted report run this exact
// component — only the DATA differs. Story 4-78 retired the second,
// hand-rolled preview-only implementation that used to live in
// `ReportDesigner.tsx` (G-17 graveyard — see that file's history).
//
// ── Why a React renderer and not an iframe of `/reports/:id/preview` ───────
// The server route streams a Puppeteer-rendered PDF (4-77). Three done-when
// clauses are unsatisfiable through it:
//   * PHONE — a PDF in an iframe does not reflow at 390px, it scrolls in two
//     axes;
//   * A11Y — the content of a cross-document PDF viewer is not in this page's
//     accessibility tree, so a heading structure and reading order cannot be
//     asserted (or used);
//   * PRINT — a print stylesheet cannot reach into another document, so
//     "print usable" would mean "the browser prints the PDF plugin", which is
//     not a thing we control.
// So the version arrives as DATA and is composed HERE, in the page, out of
// ordinary semantic elements the print stylesheet and the screen reader both
// see.
//
// ── THE RENDERER. Singular. (story 4-128f) ────────────────────────────────
// The paragraph above used to end: "The server `/preview` and `/download`
// routes are untouched and still own the PDF (a separate renderer,
// backend-owned, out of this story's scope)." That sentence — and the whole
// family of comments across this codebase that described a deliberate,
// hand-synchronised "backend twin" of this component — is no longer true, and
// P06 §4.3 requires it to stop being written down as if it were: "Update stale
// comments claiming manually synchronized renderers or different pagination
// paths are intentional."
//
// `backend/src/services/pdf/pdfExport.service.js#buildReportHtml` was that
// twin: ~1,200 lines of hand-built HTML and CSS, copied from this file by
// hand, drifting from it silently, with its own fonts, its own colour
// resolution, its own tables and its own pagination. Story 4-128f DELETED it,
// along with `buildFooterTemplate` (the Puppeteer footer frame),
// `pagedLetterheadRenderer.service.js` (the second stylesheet and second
// Paged.js invocation that existed only for `letterhead.mode:
// 'everyContentPage'`) and `reportTocPageNumbers.service.js`'s independent
// measurement pass.
//
// `/download` now renders THIS TREE. The worker loads the same
// content-addressed artifact the isolated print host loads
// (`src/report-artifact/`), awaits the same `paginate.ts#ready()` lifecycle,
// and serializes with `preferCSSPageSize: true`, `displayHeaderFooter: false`
// and zero API margins — so page size, margins, running headers/footers and
// TOC page numbers all come from this boundary's own policy and CSS, in every
// host. There is no second renderer to keep in step, and no pagination path
// that depends on which letterhead mode is set.
//
// ── `content.style` is the authoritative brand/layout source ───────────────
// `style.sectionHeaderStyle` ('filled'|'centered'|'minimal') and
// `style.accentColor` are the template's ACTUAL saved "Report layout" panel
// config (story 4-76, `report.service.js#resolveReportStyle`) — not the
// legacy top-level `content.headerStyle` ('standard'|'centered'|'minimal'),
// which the contract documents as a pre-4-76 placeholder "never wired to a
// real config". Reading `style.*` here is what makes an editor's chosen
// layout and a rendered report agree — before this story the web render
// silently ignored a template's real header-style choice.
//
// `style.pageSize` is deliberately NOT consumed inside this component: page
// framing is a CALLER concern — baking a fixed page width in here would
// break the phone reflow this component exists to provide. Callers that want
// a paper-width preview (the editor) apply their own max-width wrapper keyed
// off `style.pageSize`. `style.thumbnailSize` WAS in that same "nothing to
// size" category pre-4-80 (`ReportContentRow` carries no photo) — no longer
// true now that `mediaSummary.photos` exists, so this component threads it
// into `ReportMediaSummaryGrid` (story 4-81).
//
// ── `content.contentToggles` gates the 4-80 content blocks (story 4-81) ────
// Before this story the template editor's 11 Content-tab checkboxes were
// pure state: saved, round-tripped, read by NEITHER renderer. `resolveContent
// Toggles` (`report.service.js`) now resolves the SAME panel config the
// editor itself reads (`reportLayoutOf`), so `content.contentToggles` here is
// the author's actual choice, not a hardcoded "always show". 9 of the 11
// toggles are wired:
//   * Six gate a NEW, real 4-80 data block one-to-one — `scores` →
//     `scoreSummary`, `tableOfContents` → `tableOfContents`, `flaggedItems` →
//     `flaggedItems`, `actions` → `actions`, `mediaSummary` → `mediaSummary`
//     (grid tile size also reads `style.thumbnailSize` — see
//     `ReportMediaSummaryGrid`'s own comment), `disclaimer` → `disclaimer`
//     (rendered only once BOTH the toggle is on and the (currently
//     always-null) slot has text — a null disclaimer with the toggle on is
//     an honest "nothing configured yet", not a bug).
//   * `inspectionResults` gates every NON-table `content.sections` entry
//     (the bulk of the report — the pre-existing Q&A/room/findings content,
//     unconditional before this story) and `tables` gates every
//     `kind: 'table'` entry SEPARATELY, rendered as a real `<table>`
//     (`ReportDataTable`) rather than the flattened "Table 1"/"Table 2"
//     label/value sections a `kind: 'table'` repeat block used to produce
//     (`inspectionAnswers.service.js#buildReportAnswerView` fixed the SOURCE
//     of that bug; this only had to stop hiding the fix behind the wrong
//     markup). The two toggles are independent because a template's author
//     may want the narrative Q&A without the raw table, or vice versa.
//   * `pageBreak` — RETIRED as of story 4-110. It used to gate a real break
//     before every `kind:'page'` section (paired with `ReportContentSection
//     .kind`, wired in this same story). `ReportContentSection.pageBreak`
//     (a real, per-section, editable-after-creation property — see that
//     field's own contract comment) replaces the double-gate entirely: THIS
//     component no longer reads `toggles.pageBreak` for anything. A section
//     with `pageBreak === 'always'` gets `.report-page-break`
//     (`break-before: page`, a no-op outside pagination, so it costs
//     nothing on screen) plus a visible on-screen "Page break" divider so a
//     template author can see where one lands WITHOUT switching to an
//     actual print — `print.css` hides that divider and keeps only the real
//     break for an actual print/PDF. `pageBreak === 'avoid'` gets
//     `.report-break-avoid` (`break-before: avoid` — glued to the PREVIOUS
//     block, distinct from the unconditional `break-inside: avoid` every
//     section already carries). A section with no `pageBreak` key at all
//     (predates this story — either a live projection or an already-FROZEN
//     `assembledContent` snapshot, never rewritten in place) resolves the
//     SAME migration default `report.service.js#buildRenderContent` and
//     `pdfExport.service.js#renderSection` apply: `kind:'page'` -> `'always'`
//     (today's real behavior, unchanged), everything else -> `'auto'`
//     (unchanged).
// The remaining 2 are deliberately deferred:
//   * `footer` — the web view has no footer element AT ALL today (the PDF's
//     `.report-footer` is backend-only chrome); wiring it means adding a new
//     element, not gating an existing one, out of this story's scope.
//   * `summary` — the version's own summary PROSE lives inside the
//     "Overview" section's `body`, alongside Site/Status/Version rows that
//     are not "the summary" and should not hide with it; splitting that one
//     section into two is a real change this story does not make.
//
// COLOUR: every colour here is a semantic token except `accentColor`/`logo`,
// which are tenant DATA (the org's brand, chosen in the designer) and
// therefore arrive as values at runtime — the one thing a token cannot
// express. Accent is applied only to a border/tint, never to text, so it can
// never fail contrast against the surface (P10 §1: the org overlay may not
// touch structural tokens).

const TONE_CLASS: Record<ReportContentRow['tone'], string> = {
  neutral: 'text-muted-foreground',
  positive: 'text-success',
  warning: 'text-warning',
  critical: 'text-destructive',
};

// Story 4-84 — `style.font`'s third real choice ('mono'), additive alongside
// the original 'sans'/'serif'. 'sans' maps to '' because it's this
// component's own unstyled default (`--font-sans`, applied by `body` in
// index.css); 'serif'/'mono' step onto Tailwind's `font-serif`/`font-mono`
// utility classes — the LATTER is UNMODIFIED Tailwind default (only
// `sans`/`display` are overridden in `tailwind.config.ts`), so this is not a
// new font being loaded, just a class already available. Story 4-128f: this
// used to add that `pdfExport.service.js#FONT_STACKS` "carries the
// byte-identical CSS stack for 'mono' under the SAME key, so this is a real
// three-way match". There is no backend stack any more — that file is deleted
// and every host resolves these three classes against `fonts.css`'s own
// `--report-font-*` properties, which now name PACKAGED bytes for all three
// (defect D-11). A match by construction, not by matching literals.
const FONT_CLASS: Record<ReportStyle['font'], string> = {
  sans: '',
  serif: 'font-serif',
  mono: 'font-mono',
};

// Story 4-120 — Style Tools' colour ROLES: a fixed lookup resolving
// `ReportStyle.colorRoles` (four allowlisted enums — never a raw color
// picker) to the REAL values this component's own accent-tinted surfaces
// already know how to paint. Story 4-128f: `pdfExport.service.js
// #resolveColorRoles` used to be described here as the "byte-for-byte PDF
// twin", part of a "hand-maintained twin, no shared runtime across the
// frontend/backend boundary" pattern. That twin is deleted; this lookup is
// the only one, and the PDF is rendered from this tree.
//
//  - `brand`: the umbrella color source. 'accent' (default) is the
//    template's own `accentColor`, unchanged; 'neutral' opts the WHOLE
//    report out of accent tinting everywhere at once (`brandColor` resolves
//    `null`, and every accent-tinted surface below already has an honest
//    "nothing to tint with" fallback for a `null`/absent accent).
//  - `heading`: 'default' (unchanged) leaves heading TEXT ink alone; 'brand'
//    tints it with `deriveSecondaryTextColor(brandColor)` — the SAME
//    contrast-safe derivation story 4-82 already built for
//    `secondaryTextFromAccent`, reused here rather than a second math, so
//    heading text can never fail contrast the way a raw accent hex against
//    text could.
//  - `border`: 'default' (unchanged) leaves border/rule colors (the
//    'minimal' heading's border, `borderDensity: 'bold'`'s thicker table/
//    card borders, `variants.letterhead: 'divider'`'s rule,
//    `variants.table: 'striped'`'s tint) at their existing token/accent
//    fallback; 'brand' opts them into the raw brand color specifically.
//  - `findingSeverity`: resolved separately at each `ReportFindingsRollup`
//    call site (`severityColor` prop) — 'standard' passes `null` (today's
//    4-tone palette unchanged), 'brand' passes the SAME derived heading
//    color (falls back to 'standard' automatically when `brand` is
//    'neutral' — nothing to derive from, an honest degrade, not a silent
//    wrong color).
function resolveColorRoles(
  colorRoles: ReportStyle['colorRoles'],
  accentColor: string | null,
): { brandColor: string | null; headingColor: string | null; borderColor: string | null; findingSeverityColor: string | null } {
  const brandColor = colorRoles.brand === 'neutral' ? null : accentColor;
  const headingColor = colorRoles.heading === 'brand' ? deriveSecondaryTextColor(brandColor) : null;
  const borderColor = colorRoles.border === 'brand' ? brandColor : null;
  // Independent of `heading` above — `findingSeverity` derives its OWN
  // contrast-safe tint from `brandColor` rather than reusing whatever
  // `headingColor` happened to resolve to, so choosing 'brand' for ONE role
  // never silently activates (or is silently gated behind) the other.
  const findingSeverityColor = colorRoles.findingSeverity === 'brand' ? deriveSecondaryTextColor(brandColor) : null;
  return { brandColor, headingColor, borderColor, findingSeverityColor };
}

// ── Row `kind` → `<dd>` content (story 4-83c, 4-86) ─────────────────────────
// Mirrors `contracts/reports.js#ReportContentRow.kind` (`'text'` |
// `'photo-grid'` | `'chip'`) and `pdfExport.service.js#ROW_KIND_RENDERERS`'s
// own lookup — this story's forward-looking constraint asks for ONE table
// dispatching a resolved row's `kind` to how it renders, read here instead
// of an inline `row.kind === 'photo-grid' ? … : …` at the JSX call site, so
// a future row kind (video/audio, the implementer-scoped checkbox variant)
// is one new entry, not a rewrite of this component. `chip` reuses the
// existing `OptionChip` primitive (`components/ui/Chip.tsx`) — the same
// tinted-chip look a select field's option already renders everywhere else
// an option value is shown — rather than a new one-off component; `row.chip`
// is only ever populated with a real, non-empty `color`
// (`backend/src/config/answerDisplayRenderers.js#toChip` never emits one
// otherwise), so there is no "no color" fallback branch to render here.
const ROW_KIND_RENDERERS: Record<ReportContentRow['kind'], (row: ReportContentRow, showCaptions: boolean) => JSX.Element> = {
  text: (row) => <>{row.value}</>,
  'photo-grid': (row, showCaptions) => <ReportInlinePhotoGrid photos={row.photos ?? []} showCaptions={showCaptions} />,
  chip: (row) =>
    row.chip ? (
      <OptionChip option={row.chip.color ? { label: row.chip.label, color: row.chip.color } : { label: row.chip.label }} />
    ) : (
      <>{row.value}</>
    ),
};

// Story 4-117 — the four row-level Content Tools toggles every layout body
// below reads. Default values are BYTE-IDENTICAL to every pre-4-117 render
// (`DEFAULT_REPORT_LAYOUT.content`'s own values for these keys) — a caller
// that omits this object (every call site inside THIS component always
// supplies the real, resolved `toggles`; this default exists purely so a
// future direct caller of `ListSectionBody`/etc. cannot forget it and get a
// silent crash instead of today's behavior).
interface RowToggles {
  showQuestionLabels: boolean;
  showAnswers: boolean;
  showDescriptions: boolean;
  evidenceCaptions: boolean;
}
const DEFAULT_ROW_TOGGLES: RowToggles = {
  showQuestionLabels: DEFAULT_REPORT_LAYOUT.content.showQuestionLabels,
  showAnswers: DEFAULT_REPORT_LAYOUT.content.showAnswers,
  showDescriptions: DEFAULT_REPORT_LAYOUT.content.showDescriptions,
  evidenceCaptions: DEFAULT_REPORT_LAYOUT.content.evidenceCaptions,
};

function rowValueContent(row: ReportContentRow, showCaptions: boolean): JSX.Element {
  const renderer = ROW_KIND_RENDERERS[row.kind] ?? ROW_KIND_RENDERERS.text;
  return renderer(row, showCaptions);
}

/** Story 4-117 — "descriptions... help text" (one mechanism; see
 * `contracts/reports.js#ReportContentRow.description`'s own comment): a
 * small muted line under the label, ON only when `showDescriptions` is on
 * AND the row actually carries one. */
function RowDescription({ row, toggles }: { row: ReportContentRow; toggles: RowToggles }): JSX.Element | null {
  if (!toggles.showDescriptions || !row.description) return null;
  return <p className="text-caption text-muted-foreground">{row.description}</p>;
}

// ── Section Presentation Tools (story 4-116) ────────────────────────────────
// `ReportContentSection.presentation` is ALWAYS fully resolved on the wire
// (`agents/shared/formSchema.js#resolveSectionPresentation`) — this
// component reads every field directly, no further fallback. `pdfExport
// .service.js` is the byte-for-byte PDF twin of everything below (same
// class names translated to plain CSS, same layout/density/heading/
// showEmpty/evidencePlacement/repeatHeading behavior) — see that file's own
// header comment for the "one shared rendering seam" requirement this
// story, like every Report Studio story before it, has to satisfy.
type SectionPresentation = ReportContentSection['presentation'];

/** `section.presentation` resolved defensively — the SAME "an already-
 * FROZEN `assembledContent` snapshot has no key at all" reasoning
 * `pageBreak` already applies elsewhere in this component: every version
 * rendered before this story has no `presentation` field on any of its
 * sections, and the report-detail/share pages serve a `ready` version's
 * content AS-IS, never rewritten in place. `pdfExport.service.js`'s own
 * `renderSection` applies the identical fallback. */
function presentationOf(section: ReportContentSection): SectionPresentation {
  return section.presentation ?? DEFAULT_SECTION_PRESENTATION;
}

/** Story 4-119 — `content.mediaSummaryPresentation` resolved defensively,
 * the SAME "an already-frozen `assembledContent` snapshot has no key at
 * all" reasoning `presentationOf` above applies: every version rendered
 * before this story has no `mediaSummaryPresentation` field at all.
 * `pdfExport.service.js`'s own `mediaSummaryPresentation = content.
 * mediaSummaryPresentation || DEFAULT_MEDIA_SUMMARY_PRESENTATION` applies
 * the identical fallback, `DEFAULT_REPORT_LAYOUT.mediaSummary` being the
 * one shared, byte-identical default object both read (`agents/shared/
 * formSchema.js`). */
function mediaSummaryPresentationOf(content: ReportContent): ReportContent['mediaSummaryPresentation'] {
  return content.mediaSummaryPresentation ?? DEFAULT_REPORT_LAYOUT.mediaSummary;
}

/** An empty row, per showEmpty's own definition of "empty" — no answer text,
 * no chip, no photos. */
function isRowEmpty(row: ReportContentRow): boolean {
  if (row.kind === 'photo-grid') return !row.photos || row.photos.length === 0;
  if (row.kind === 'chip') return !row.chip && !row.value;
  return !row.value;
}

/** `showEmpty === 'notProvided'` substitutes literal text for an empty row's
 * value; `'show'` (default) and `'hide'` (rows already filtered out by the
 * caller before this runs) both fall through to the normal renderer. */
function rowDisplayValue(
  row: ReportContentRow,
  showEmpty: SectionPresentation['showEmpty'],
  toggles: RowToggles = DEFAULT_ROW_TOGGLES,
): JSX.Element {
  if (showEmpty === 'notProvided' && isRowEmpty(row)) {
    return <span className="italic text-muted-foreground">Not provided</span>;
  }
  return rowValueContent(row, toggles.evidenceCaptions);
}

/** `heading === 'hidden'` -> `null` (caller skips rendering the heading
 * entirely); `'renamed'` uses `headingLabel` (falls back to the template
 * name if blank — an honest default, not a disabled state); `'numbered'`
 * prefixes the section's 1-based REPORT-order position; `'visible'`
 * (default) is the section's own template name, unchanged. */
function sectionHeadingText(section: ReportContentSection, reportIndex: number): string | null {
  const p = presentationOf(section);
  if (p.heading === 'hidden') return null;
  if (p.heading === 'renamed') return p.headingLabel || section.heading;
  if (p.heading === 'numbered') return `${reportIndex + 1}. ${section.heading}`;
  return section.heading;
}

// Story 4-116 — `density`: 'comfortable' (default) is BYTE-IDENTICAL to
// every pre-4-116 render (`gap-x-6 gap-y-1`, the exact classes this <dl>
// already hardcoded); 'compact'/'condensed' are real, tighter spacing
// presets, the per-section sibling `headingScale`/`thumbnailSize` already
// established at the report-wide level.
const DENSITY_DL_CLASS: Record<SectionPresentation['density'], string> = {
  comfortable: 'gap-x-6 gap-y-1',
  compact: 'gap-x-4 gap-y-0.5',
  condensed: 'gap-x-3 gap-y-0',
};
const DENSITY_TEXT_CLASS: Record<SectionPresentation['density'], string> = {
  comfortable: 'text-sm',
  compact: 'text-sm',
  condensed: 'text-xs',
};

/** `evidencePlacement === 'gallery'`: pool every row's photos into ONE grid
 * rendered once at the end of the section, emptying each photo row's own
 * cell in place (still listed, so its LABEL stays legible, just with no
 * per-row grid). Pure — never mutates `rows`. */
function partitionForGallery(rows: ReportContentRow[]): {
  rows: ReportContentRow[];
  galleryPhotos: ReportEvidencePhotoItem[] | null;
} {
  const galleryPhotos = rows.flatMap((row) => (row.kind === 'photo-grid' ? row.photos ?? [] : []));
  const strippedRows = rows.map((row): ReportContentRow =>
    row.kind === 'photo-grid' ? { ...row, kind: 'text', value: '', photos: null } : row
  );
  return { rows: strippedRows, galleryPhotos: galleryPhotos.length > 0 ? galleryPhotos : null };
}

/**
 * Story 4-117 — "move supporting documents to an appendix": the real
 * end-of-report structure `4-116`'s own `evidencePlacement:'appendix'` was
 * deferred for lack of (see that story's DONE annotation). Pooled at RENDER
 * time from `content.sections[].rows`/`.presentation`, the SAME "no wire
 * shape change needed" approach `evidencePlacement:'gallery'` already
 * established — `pdfExport.service.js#collectAppendixPhotosPdf` is this
 * function's PDF twin, byte-for-byte the same selection rule.
 *
 * Scoped to `layout: 'list'` sections only, matching the ONLY layout that
 * actually consults `evidencePlacement` for stripping today (`narrative`/
 * `summaryCards` ignore it entirely — see `NarrativeSectionBody`'s own
 * header comment): a narrative/summaryCards section choosing `'appendix'`
 * would otherwise render its photos BOTH inline (those two layouts never
 * strip them) AND here, a real duplicate-content bug this scope avoids.
 *
 * `ReportMediaSummaryPhoto` shape (`roomName`/`thumbnailUrl`/`caption`),
 * reusing `ReportMediaSummaryGrid` rather than a second grid primitive —
 * `roomName` carries the section+row it left, since an appendix photo has
 * lost its original position and needs that context restated (an inline
 * photo sits right under its own row's label already).
 */
function appendixPhotosOf(sections: ReportContentSection[]): ReportMediaSummaryPhoto[] {
  const photos: ReportMediaSummaryPhoto[] = [];
  sections.forEach((section) => {
    const presentation = presentationOf(section);
    if (presentation.layout !== 'list' || presentation.evidencePlacement !== 'appendix') return;
    section.rows.forEach((row) => {
      if (row.kind !== 'photo-grid') return;
      (row.photos ?? []).forEach((photo) => {
        photos.push({ roomName: `${section.heading} — ${row.label}`, thumbnailUrl: photo.thumbnailUrl, caption: photo.caption });
      });
    });
  });
  return photos;
}

/**
 * The default 'list' layout's row grid — same `<dl>` markup every report
 * has always rendered, extended with `density`/`showEmpty`/`evidencePlacement`,
 * and a real `columns:'two'` split.
 *
 * Edge case (this story's own): "a two-column layout contains an item that
 * only supports full width" — an `inline`-placed photo-grid row cannot sit
 * inside a narrow label/value column pair, so it is always pulled out and
 * rendered full-width below the two-column grid, never disabled/dropped.
 */
function ListSectionBody({
  section,
  toggles = DEFAULT_ROW_TOGGLES,
  captionScale = 'sm',
}: {
  section: ReportContentSection;
  toggles?: RowToggles;
  /** Story 4-120 — `typographyRoles.caption`, see `CAPTION_SCALE_CLASS`'s own comment. */
  captionScale?: ReportStyle['typographyRoles']['caption'];
}): JSX.Element | null {
  const labelClass = CAPTION_SCALE_CLASS[captionScale] ?? CAPTION_SCALE_CLASS.sm;
  const presentation = presentationOf(section);
  const placement = presentation.evidencePlacement;
  // Story 4-117 — `'appendix'`: pooled OUT of this section's own rows the
  // SAME way `'gallery'` already is (`partitionForGallery`), but the pooled
  // photos are DISCARDED here rather than rendered locally — this
  // component's own `appendixPhotosOf` (below) independently re-collects
  // them for the report-level "Supporting documents" block this component
  // renders once, at the very end of the whole report — rendering them
  // again here would duplicate every appendix-placed photo.
  const pooled = placement === 'gallery' || placement === 'appendix';
  const { rows: sourceRows, galleryPhotos: pooledPhotos } = pooled
    ? partitionForGallery(section.rows)
    : { rows: section.rows, galleryPhotos: null };
  const galleryPhotos = placement === 'gallery' ? pooledPhotos : null;
  const visibleRows = presentation.showEmpty === 'hide' ? sourceRows.filter((row) => !isRowEmpty(row)) : sourceRows;

  if (visibleRows.length === 0 && !galleryPhotos) return null;

  const dlClass = `grid ${DENSITY_DL_CLASS[presentation.density]} sm:grid-cols-[max-content,1fr]`;
  const textClass = DENSITY_TEXT_CLASS[presentation.density];

  const renderRow = (row: ReportContentRow, index: number, keyPrefix: string): JSX.Element[] => {
    // `afterResponse`: a photo-grid row's grid moves OUT of its own `<dd>`
    // into a full-width block immediately following it, still per-row —
    // distinct from `inline` (grid stays in the `<dd>`) and `gallery`
    // (pooled across the whole section, handled by `partitionForGallery`
    // before this function ever sees the row).
    const isPhotoAfter = placement === 'afterResponse' && row.kind === 'photo-grid';
    const nodes: JSX.Element[] = [
      <div key={`${keyPrefix}-${index}`} className="contents">
        {/* Real bug, found live (2026-09-08) — `sm:grid-cols-[max-content,1fr]`
            (below) gives the label column no ceiling: this report's question
            labels repeat the full question text with a suffix ('— Photo
            evidence'/'— Notes'), often 60-80 characters, so `max-content`
            (measured as one unbroken line) could eat most of the row's
            width and leave the value column too narrow for a real notes
            paragraph — `pdfExport.service.js`'s own PDF twin had the exact
            same bug, live-verified there via a rendered page image (a
            633px-tall three-sentence notes row). `sm:max-w-[220px]` caps the
            label column exactly like that fix's own `.row-grid > .row-label`
            CSS rule, so it wraps instead of crowding out the value. */}
        <dt
          className={`${labelClass} font-semibold uppercase tracking-wide text-muted-foreground sm:max-w-[220px] ${toggles.showQuestionLabels ? '' : 'sr-only'}`}
        >
          {row.label}
          <RowDescription row={row} toggles={toggles} />
        </dt>
        <dd className={`${textClass} ${TONE_CLASS[row.tone]}`}>
          {!toggles.showAnswers || isPhotoAfter ? null : rowDisplayValue(row, presentation.showEmpty, toggles)}
        </dd>
      </div>,
    ];
    if (toggles.showAnswers && isPhotoAfter && row.photos && row.photos.length > 0) {
      nodes.push(
        <div key={`${keyPrefix}-${index}-photos`} className="col-span-2 -mt-1">
          <ReportInlinePhotoGrid photos={row.photos} showCaptions={toggles.evidenceCaptions} />
        </div>
      );
    }
    return nodes;
  };

  if (presentation.columns === 'two') {
    const fullWidthRows = placement === 'inline' ? visibleRows.filter((row) => row.kind === 'photo-grid') : [];
    const columnableRows = placement === 'inline' ? visibleRows.filter((row) => row.kind !== 'photo-grid') : visibleRows;
    const mid = Math.ceil(columnableRows.length / 2);
    const left = columnableRows.slice(0, mid);
    const right = columnableRows.slice(mid);
    return (
      <>
        <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
          <dl className={dlClass}>{left.flatMap((row, i) => renderRow(row, i, 'col-left'))}</dl>
          <dl className={dlClass}>{right.flatMap((row, i) => renderRow(row, i, 'col-right'))}</dl>
        </div>
        {toggles.showAnswers &&
          fullWidthRows.map((row, i) => (
            <div key={`full-width-${i}`} className="mt-2">
              <p className={`mb-1 ${labelClass} font-semibold uppercase tracking-wide text-muted-foreground`}>{row.label}</p>
              <ReportInlinePhotoGrid photos={row.photos ?? []} showCaptions={toggles.evidenceCaptions} />
            </div>
          ))}
        {galleryPhotos && <ReportInlinePhotoGrid photos={galleryPhotos} showCaptions={toggles.evidenceCaptions} />}
      </>
    );
  }

  return (
    <>
      <dl className={dlClass}>{visibleRows.flatMap((row, i) => renderRow(row, i, 'row'))}</dl>
      {galleryPhotos && <ReportInlinePhotoGrid photos={galleryPhotos} showCaptions={toggles.evidenceCaptions} />}
    </>
  );
}

/**
 * `layout: 'narrative'` — a real, honestly-scoped new layout: flowing prose
 * built from the section's own label/value rows ("Site condition: Good.
 * Roof: Minor wear.") instead of a label/value grid. Photos have no prose
 * form, so they always render as a pooled grid after the paragraph — the
 * only coherent placement for evidence inside running text, regardless of
 * `evidencePlacement` (documented judgment call, `ReportStudioPanels.tsx`
 * disables the control here rather than pretending 4 placements exist).
 *
 * Story 4-117 — `showQuestionLabels`/`showAnswers`/`showDescriptions` are
 * deliberately NOT consulted here, same judgment call as `evidencePlacement`
 * immediately above: narrative prose fuses a row's label and value into one
 * sentence with no separate slots to hide either half of, or to append a
 * description to without reading as a run-on. `evidenceCaptions` still
 * applies to its pooled photos, the one part of this layout that IS a real
 * photo grid.
 */
function NarrativeSectionBody({
  section,
  toggles = DEFAULT_ROW_TOGGLES,
}: {
  section: ReportContentSection;
  toggles?: RowToggles;
}): JSX.Element | null {
  const presentation = presentationOf(section);
  const proseRows = section.rows.filter((row) => row.kind !== 'photo-grid');
  const photoRows = section.rows.filter((row) => row.kind === 'photo-grid');
  const visibleRows = presentation.showEmpty === 'hide' ? proseRows.filter((row) => !isRowEmpty(row)) : proseRows;
  const sentences = visibleRows.map((row) => {
    const empty = isRowEmpty(row);
    if (empty) return `${row.label}: ${presentation.showEmpty === 'notProvided' ? 'Not provided' : '—'}`;
    const text = row.kind === 'chip' && row.chip ? row.chip.label : row.value;
    return `${row.label}: ${text}`;
  });
  const photos = photoRows.flatMap((row) => row.photos ?? []);
  if (sentences.length === 0 && photos.length === 0) return null;
  return (
    <>
      {sentences.length > 0 && <p className="text-sm leading-relaxed text-foreground">{sentences.join('. ')}.</p>}
      {photos.length > 0 && <ReportInlinePhotoGrid photos={photos} showCaptions={toggles.evidenceCaptions} />}
    </>
  );
}

/**
 * `layout: 'summaryCards'` — a real, honestly-scoped new layout: each row
 * becomes its own bordered card (label above value) in a responsive grid,
 * instead of a label/value list. `evidencePlacement` is not consulted — a
 * photo-grid row's own grid renders inside its own card either way, the
 * same "inline within its row" shape `inline` already means for the list
 * layout, so there is nothing to move.
 */
function SummaryCardsSectionBody({
  section,
  toggles = DEFAULT_ROW_TOGGLES,
  captionScale = 'sm',
}: {
  section: ReportContentSection;
  toggles?: RowToggles;
  /** Story 4-120 — `typographyRoles.caption`, see `CAPTION_SCALE_CLASS`'s own comment. */
  captionScale?: ReportStyle['typographyRoles']['caption'];
}): JSX.Element | null {
  const presentation = presentationOf(section);
  const rows = presentation.showEmpty === 'hide' ? section.rows.filter((row) => !isRowEmpty(row)) : section.rows;
  if (rows.length === 0) return null;
  const labelClass = CAPTION_SCALE_CLASS[captionScale] ?? CAPTION_SCALE_CLASS.sm;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {rows.map((row, index) => (
        <div key={index} className="rounded-md border border-border bg-muted/30 p-3">
          {toggles.showQuestionLabels && (
            <p className={`${labelClass} font-semibold uppercase tracking-wide text-muted-foreground`}>
              {row.label}
              <RowDescription row={row} toggles={toggles} />
            </p>
          )}
          {toggles.showAnswers && (
            <div className={`mt-1 ${TONE_CLASS[row.tone]}`}>{rowDisplayValue(row, presentation.showEmpty, toggles)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// Story 4-82 — `headingScale` steps the type size around the EXISTING
// per-`headerStyle` size each variant already hardcoded. 'md' below is
// exactly those pre-4-82 literals (`text-base` centered, `text-sm` filled/
// minimal), so a template that never touches this control renders at the
// identical size it did before this story — no regression.
const HEADING_SCALE_TEXT_CLASS: Record<ReportStyle['headingScale'], { filled: string; centered: string; minimal: string }> = {
  sm: { filled: 'text-xs', centered: 'text-sm', minimal: 'text-xs' },
  md: { filled: 'text-sm', centered: 'text-base', minimal: 'text-sm' },
  lg: { filled: 'text-base', centered: 'text-lg', minimal: 'text-base' },
};

// Story 4-85 — `underlineWidth`: 'full' (default) adds no class at all, since
// a 'minimal' heading's border-bottom / a 'filled' heading's tinted bar are
// already block-level (their own container's full width) with nothing here
// overriding `display` — a template that never touches this control renders
// byte-identically to before this story. 'text' adds `inline-block`,
// shrinking either rule to hug only the heading's own text. 'centered' draws
// no rule either way (see this component's own header comment), so the class
// is never applied there. `radius`: corner treatment for the 'filled'
// variant's tinted bar only ('rounded', default, is the pre-4-85 `rounded-md`
// unchanged; 'sharp' drops to `rounded-none`) — the PDF twin
// (`pdfExport.service.js#renderSectionHeading`) applies the identical pair of
// rules under the same class name / radius value.
const UNDERLINE_WIDTH_CLASS: Record<ReportStyle['sectionUnderlineWidth'], string> = {
  full: '',
  text: 'inline-block',
};

// Story 4-120 — `typographyRoles.caption`: the question-label/`dt` text size,
// hardcoded `text-xs` regardless of `headingScale`/section `density` since
// 4-76. 'sm' (default) is that exact pre-4-120 literal, byte-identical;
// 'md' steps it up one notch. Threaded into `ListSectionBody`/
// `SummaryCardsSectionBody` below (the two layouts with a real question-
// label element) — `NarrativeSectionBody` fuses label+value into prose with
// no separate label slot, so has nothing to apply this to.
const CAPTION_SCALE_CLASS: Record<ReportStyle['typographyRoles']['caption'], string> = {
  sm: 'text-xs',
  md: 'text-sm',
};

function SectionHeading({
  text,
  headerStyle,
  accent,
  headingScale = 'md',
  underlineWidth = 'full',
  radius = 'rounded',
  headingColor = null,
  borderColor = null,
}: {
  text: string;
  headerStyle: ReportStyle['sectionHeaderStyle'];
  accent: string | null;
  headingScale?: ReportStyle['headingScale'];
  underlineWidth?: ReportStyle['sectionUnderlineWidth'];
  radius?: ReportStyle['surfaceRadius'];
  /** Story 4-120 — `colorRoles.heading: 'brand'` resolved color (already
   *  contrast-safe-derived at the call site); `null` leaves heading text ink unchanged. */
  headingColor?: string | null;
  /** Story 4-120 — `colorRoles.border: 'brand'` resolved color, overrides
   *  the 'minimal' variant's border (which otherwise falls back to `accent`,
   *  its pre-4-120 behavior); `null` leaves that fallback unchanged. */
  borderColor?: string | null;
}): JSX.Element {
  const sizeClass = HEADING_SCALE_TEXT_CLASS[headingScale] ?? HEADING_SCALE_TEXT_CLASS.md;
  const underlineClass = UNDERLINE_WIDTH_CLASS[underlineWidth] ?? '';
  const textColorStyle = headingColor ? { color: headingColor } : undefined;
  if (headerStyle === 'centered') {
    return (
      <h3
        className={`report-section-heading text-center font-bold ${headingColor ? '' : 'text-foreground'} ${sizeClass.centered}`}
        style={textColorStyle}
      >
        {text}
      </h3>
    );
  }
  if (headerStyle === 'minimal') {
    const resolvedBorder = borderColor ?? accent;
    return (
      <h3
        className={`report-section-heading ${underlineClass} border-b pb-1 font-bold ${headingColor ? '' : 'text-foreground'} ${sizeClass.minimal}`}
        style={{ ...(resolvedBorder ? { borderColor: resolvedBorder } : {}), ...textColorStyle }}
      >
        {text}
      </h3>
    );
  }
  // 'filled' — the DefectSnap default: a tinted bar.
  return (
    <h3
      className={`report-section-heading ${underlineClass} ${
        radius === 'sharp' ? 'rounded-none' : 'rounded-md'
      } px-3 py-1.5 font-bold ${headingColor ? '' : 'text-foreground'} ${sizeClass.filled}`}
      style={{ ...(accent ? { backgroundColor: `${accent}14` } : {}), ...textColorStyle }}
    >
      {text}
    </h3>
  );
}

export interface ReportDocumentProps {
  content: ReportContent;
  // 'screen' (default) is the on-screen flat bg-card surface (review/report-detail,
  // and the template editor's "Web" preview tab; story 2-147d converted this from
  // glass-card to flat). 'print' swaps that surface for the SAME flattening
  // `print.css` already applies when a reviewer hits
  // Ctrl+P on this exact markup (`.report-render` → plain white,
  // no blur, no shadow) — applied via a class instead of a `@media print`
  // query so the template editor's "Print" preview tab (story 4-77 follow-up)
  // can show it live, on screen, without actually printing. It is also
  // deliberately the same flat-white/ink-on-paper target the PDF uses —
  // which, since story 4-128f, is not a matching claim at all: the worker
  // renders THIS tree with `variant: 'print'`. The template author previewing
  // their layout and the downloaded PDF are the same markup and the same CSS.
  variant?: 'screen' | 'print';
  // Story 4-111 — `PagedReportPreview.tsx` ONLY (see `ReportTableOfContents`'s
  // own header comment on why that ONE caller cannot know page numbers before
  // its own pagination pass runs against this exact markup). Every other
  // caller leaves this off, unaffected.
  pendingTocPageNumbers?: boolean;
  // Story 4-113 (Report Studio foundation) — OPTIONAL, additive selection
  // hooks. `undefined` (every caller except the new Report Studio canvas:
  // report-detail, share, and `PagedReportPreview`'s own OWN internal
  // `renderToStaticMarkup` pass below) means this component behaves exactly
  // as before — no click handler, no ring, `data-report-section-id` is the
  // only thing that always renders (harmless, inert markup any consumer can
  // ignore). This keeps the "renderer components never read editor-local
  // state" rule literally true: the editor's selection lives in
  // `TemplateEditorPage`'s own state and reaches this component only as a
  // plain, explicit, one-way prop — never a context/store this component
  // reaches into on its own.
  selectedSectionKey?: string | null;
  onSelectSection?: (sectionKey: string) => void;
  /**
   * Story 4-128e — the trial mark's TEXT, or null/undefined for no mark.
   *
   * A string rather than a boolean because this component must not own the
   * wording of a billing mark any more than it owns the decision to show one;
   * both come from the caller, already resolved. Today the only caller that
   * passes it is the PDF worker, with `'TRIAL VERSION'` — the exact literal
   * `pdfExport.service.js` printed before this story, so no trial customer's
   * document changes. See the element's own comment at the end of this file.
   */
  watermark?: string | null;
}

export function ReportDocument({
  content,
  variant = 'screen',
  pendingTocPageNumbers = false,
  selectedSectionKey = null,
  onSelectSection,
  watermark = null,
}: ReportDocumentProps): JSX.Element {
  const accent = content.style.accentColor ?? content.accentColor;
  // Story 4-120 — Style Tools. Defensive fallback (`?? DEFAULT_REPORT_LAYOUT
  // .xxx`) for an already-frozen `assembledContent` snapshot pinned before
  // this story existed — the SAME "an older version has no key at all"
  // reasoning `mediaSummaryPresentationOf`/`presentationOf` already apply
  // elsewhere in this component.
  const colorRoles = content.style.colorRoles ?? DEFAULT_REPORT_LAYOUT.colorRoles;
  const borderDensity = content.style.borderDensity ?? DEFAULT_REPORT_LAYOUT.borderDensity;
  const styleVariants = content.style.variants ?? DEFAULT_REPORT_LAYOUT.variants;
  const typographyRoles = content.style.typographyRoles ?? DEFAULT_REPORT_LAYOUT.typographyRoles;
  const { brandColor, headingColor, borderColor, findingSeverityColor } = resolveColorRoles(colorRoles, accent);
  // A `content.contentToggles` that is MISSING (a report version assembled
  // before this story existed, still served from `assembledContent`) merges
  // against the editor's own defaults, not `{}` — `{}` would silently turn
  // `inspectionResults` off and blank that version's own main content, which
  // was rendered unconditionally before this story. Same default source
  // `report.service.js#resolveContentToggles` reads for a template with no
  // saved Content-tab config — not a second, competing default set.
  const toggles = { ...DEFAULT_REPORT_LAYOUT.content, ...content.contentToggles };
  // Story 4-117 — the row-level subset of `toggles` every section-body
  // renderer reads, extracted once so each `<Section*Body>` call site below
  // does not repeat the same four-key pick.
  const rowToggles: RowToggles = {
    showQuestionLabels: toggles.showQuestionLabels,
    showAnswers: toggles.showAnswers,
    showDescriptions: toggles.showDescriptions,
    evidenceCaptions: toggles.evidenceCaptions,
  };
  // Story 4-117 — see `appendixPhotosOf`'s own comment below.
  const appendixPhotos = appendixPhotosOf(content.sections);
  // Story 4-119 — `layout: 'appendix'` moves the WHOLE media-summary block
  // (heading, TOC entry, grid) to the true end of the document, below the
  // `appendixPhotos` "Supporting documents" block above (a DIFFERENT,
  // unrelated per-section structure — `4-116`/`4-117`'s own
  // `evidencePlacement: 'appendix'`, see this component's own JSX comment
  // at the render site for the full reasoning) under a distinct "Photo
  // appendix" identity, matching `pdfExport.service.js`'s identical
  // positioning logic byte-for-byte.
  const mediaSummaryPresentation = mediaSummaryPresentationOf(content);
  const mediaSummaryIsAppendix = mediaSummaryPresentation.layout === 'appendix';
  const mediaSummaryBlock = toggles.mediaSummary && content.mediaSummary && (
    // Story 4-87 — same `report-section`/`SectionHeading` treatment every
    // `content.sections` entry gets above (and `ReportFindingsRollup`'s own
    // heading below): before this story the grid rendered bare, with no
    // heading and no section wrapper, directly under the (unrelated)
    // content it happened to follow.
    // Story 4-111 — `data-toc-key="media-summary"` matches
    // `tableOfContentsOf`'s own hardcoded trailing entry key
    // (`report.service.js`, story 4-87) — see `pdfExport.service.js`'s
    // identical PDF-side attribute for the full reasoning. Story 4-119 —
    // `photo-appendix`/"Photo appendix" when `layout: 'appendix'`, matching
    // `report.service.js#tableOfContentsOf`'s own resolution.
    <section
      className="report-section space-y-2"
      data-toc-key={mediaSummaryIsAppendix ? 'photo-appendix' : 'media-summary'}
    >
      <SectionHeading
        text={mediaSummaryIsAppendix ? 'Photo appendix' : 'Media summary'}
        headerStyle={content.style.sectionHeaderStyle}
        accent={brandColor}
        headingScale={content.style.headingScale}
        underlineWidth={content.style.sectionUnderlineWidth}
        radius={content.style.surfaceRadius}
        headingColor={headingColor}
        borderColor={borderColor}
      />
      <ReportMediaSummaryGrid
        totalPhotos={content.mediaSummary.totalPhotos}
        roomsWithPhotos={content.mediaSummary.roomsWithPhotos}
        photos={content.mediaSummary.photos}
        thumbnailSize={content.style.thumbnailSize}
        layout={mediaSummaryPresentation.layout}
        imageFit={mediaSummaryPresentation.imageFit}
        showCaptions={mediaSummaryPresentation.showCaptions}
        showTimestamps={mediaSummaryPresentation.showTimestamps}
        groupBy={mediaSummaryPresentation.groupBy}
      />
    </section>
  );
  // Story 4-82 — scoped to the 'print' variant ONLY (a guaranteed-white
  // surface, see `reportColor.ts`'s header comment for why 'screen' cannot
  // use this: its flat bg-card background is theme-dependent, not the
  // guaranteed-white fixed color this derivation requires to validate against).
  // Story 4-120 — `brandColor`, not raw `accent`: `colorRoles.brand:
  // 'neutral'` opts the WHOLE report out of accent tinting, this
  // pre-existing 4-82 derivation included, so the one switch is honestly
  // total rather than leaving one accent consumer unaffected by it.
  const secondaryColor =
    variant === 'print' && content.style.secondaryTextFromAccent ? deriveSecondaryTextColor(brandColor) : null;
  // The app's `.dark` class scopes `text-foreground`/`text-muted-foreground`
  // to light TEXT (for a dark background) — forcing this surface to white
  // without also pinning those two classes to fixed ink colors would render
  // near-invisible light-on-white text whenever a template author previews
  // in dark mode. Tone colors (TONE_CLASS, below) are deliberately NOT
  // pinned here: `--success`/`--warning`/`--destructive` stay legible on
  // white in both themes (checked against `tokens.css`), and keeping them
  // token-driven is what makes this preview match the real backend PDF
  // renderer, which reads the SAME light-theme values.
  const surfaceClass =
    variant === 'print'
      ? 'bg-white shadow-md ring-1 ring-black/10 [&_.text-foreground]:text-neutral-900 [&_.text-muted-foreground]:text-neutral-500'
      : 'bg-card';
  // Whether `<ReportLetterhead>` below will actually render something —
  // mirrors that component's own null-render check, so the cover-page break
  // can be placed on whichever element is really first. (Story 4-128f: this
  // used to cite `pdfExport.service.js`'s `letterheadHeaderOn` as the
  // hand-maintained backend twin of the same idiom. Deleted with the rest of
  // that renderer.)
  const letterhead = content.style.letterhead;
  const letterheadAnyLogoOn = Boolean(
    letterhead && (['logoLeft', 'logoCenter', 'logoRight'] as const).some((key) => letterhead[key]?.enabled)
  );
  const letterheadTitleOn = Boolean(letterhead?.title?.enabled);
  const letterheadAddressOn = Boolean(letterhead?.addressBlock?.enabled && content.subjectAddress);
  const letterheadHeaderOn = letterheadAnyLogoOn || letterheadTitleOn || letterheadAddressOn;
  return (
    // `report-render` is the print stylesheet's single hook (see print.css):
    // printing the detail page prints THIS, and the chrome around it is hidden.
    <article
      className={`report-render ${surfaceClass} space-y-6 p-6 ${FONT_CLASS[content.style.font] ?? ''}`}
      aria-label="Report content"
    >
      {/* Story 4-106 — `reportPrint.css`'s own `@page` rule has no `size:`
          (browsers have no way to bake a per-template value into a static
          stylesheet), so a real browser print/"Save as PDF" of this page
          always used whatever paper size the OS/browser defaulted to,
          ignoring the template's own configured `pageSize` entirely — while
          the `/download` PDF endpoint (`pdfExport.service.js`) correctly
          reads it into Puppeteer's own `format` option. `'A4'`/`'letter'`
          (`contracts/reports.js`'s `ReportStyle.pageSize` enum) are already
          valid CSS Paged Media `size` keywords verbatim, so no lookup table
          is needed here — only `@page` is emitted (inert outside an actual
          print/print-to-PDF context, harmless on screen). */}
      {/* Story 4-114 — orientation appended as the real CSS Paged Media
          `landscape`/`portrait` keyword (both valid verbatim, same "no
          lookup table needed" reasoning as `pageSize` above), and `margin`
          now comes from the resolved policy's own margin preset instead of
          `reportPrint.css`'s static `16mm` fallback — this rule renders
          LATER in the document than that stylesheet, so it wins the
          cascade for a template that sets either control (harmless/inert
          outside an actual print/print-to-PDF context, same as `size`). */}
      <style>{`@page { size: ${content.style.pageSize === 'letter' ? 'letter' : 'A4'} ${
        content.style.pageOrientation === 'landscape' ? 'landscape' : 'portrait'
      }; margin: ${content.layoutPolicy.marginsMm.top}mm ${content.layoutPolicy.marginsMm.right}mm ${
        content.layoutPolicy.marginsMm.bottom
      }mm ${content.layoutPolicy.marginsMm.left}mm; }`}</style>
      {content.style.coverPage && (
        <ReportCoverPage
          title={content.title}
          templateName={content.templateName}
          templateVersion={content.templateVersion}
          subjectName={content.subjectName}
          subjectAddress={content.subjectAddress}
          logo={content.style.logo}
          accent={brandColor}
          pageSize={content.style.pageSize}
          orientation={content.style.pageOrientation}
          variant={styleVariants.cover}
        />
      )}
      {/* Story 4-88b — the NEW letterhead header slots (logoLeft/Center/
          Right, title, addressBlock), additive alongside the pre-existing
          single-`logo` header immediately below (untouched). Same element
          order the PDF uses, because since story 4-128f the PDF IS this
          element order: coverPage -> letterhead -> .report-header. Renders
          nothing when no header slot is enabled — see `ReportLetterhead`'s
          own header comment. `pageBreakBefore`: the break belongs on
          whichever element is actually FIRST after the cover page (see that
          prop's own comment for the real bug this closes). */}
      <ReportLetterhead
        letterhead={content.style.letterhead}
        title={content.title}
        subjectAddress={content.subjectAddress}
        pageBreakBefore={Boolean(content.style.coverPage) && letterheadHeaderOn}
        // Story 4-108 — see `ReportLetterheadProps.markBodyStart`'s own
        // comment. Whichever of this component or the `<header>` below is
        // actually first (the SAME `letterheadHeaderOn` boolean the break
        // class above already keys off) carries the marker — never both.
        markBodyStart={letterheadHeaderOn}
        variant={styleVariants.letterhead}
        dividerColor={borderColor}
      />
      {/* Story 4-82 — same `report-page-break` class/CSS `renderSection`
          already uses for a `kind:'page'` section (print.css, scoped to
          `body.report-print`): reused so the header (and everything after
          it) starts a fresh page when a cover page precedes it, not a
          second break mechanism. Suppressed here when the letterhead above
          already claimed the break (real bug fixed 2026-08-22 — see its own
          `pageBreakBefore` comment): a cover page can only be followed by
          ONE fresh page, not two. */}
      <header
        className={`space-y-2 ${content.style.coverPage && !letterheadHeaderOn ? 'report-page-break' : ''}`}
        // Story 4-108 — the marker lands here instead of on `ReportLetterhead`
        // exactly when that component rendered nothing (mirrors the break
        // class above; see `ReportLetterheadProps.markBodyStart`'s comment).
        data-report-body-start={letterheadHeaderOn ? undefined : 'true'}
      >
        {content.style.logo && (
          <ReportLogoImage
            src={content.style.logo}
            alt={content.templateName ? `${content.templateName} logo` : 'Report logo'}
            fallbackText={content.templateName || content.title || 'Report logo'}
            className="max-h-12 max-w-[160px] object-contain"
          />
        )}
        <h2 className="text-lg font-semibold text-foreground">{content.title}</h2>
        {content.templateName && (
          <p
            className={secondaryColor ? 'text-xs' : 'text-xs text-muted-foreground'}
            style={secondaryColor ? { color: secondaryColor } : undefined}
          >
            {`Template: ${content.templateName}${content.templateVersion == null ? '' : ` v${content.templateVersion}`}`}
          </p>
        )}
      </header>

      {toggles.scores && content.scoreSummary && (
        <ReportScoreStrip
          earned={content.scoreSummary.earned}
          possible={content.scoreSummary.possible}
          percentage={content.scoreSummary.percentage}
        />
      )}

      {toggles.tableOfContents && content.tableOfContents.length > 0 && (
        <ReportTableOfContents entries={content.tableOfContents} pendingPageNumbers={pendingTocPageNumbers} />
      )}

      {/* Story 4-117 — see `ReportContentToggles.methodology`'s own comment
          for why "introduction" gets no sibling block here. */}
      {toggles.methodology && content.methodology && <ReportDisclaimer text={content.methodology} title="Methodology" />}

      {content.sections
        // `table`-kind sections are gated by `tables`, everything else by
        // `inspectionResults` — two independent switches now that `kind`
        // exists on the wire (a template author may want the narrative Q&A
        // without the raw table data, or vice versa).
        .filter((section) => (section.kind === 'table' ? toggles.tables : toggles.inspectionResults))
        .map((section, sectionIndex) => {
          // Story 4-110 — resolve a possibly-missing `pageBreak` the same
          // way every other renderer/resolver in this family does (see this
          // component's own header comment above for the full rationale).
          const pageBreak = section.pageBreak ?? (section.kind === 'page' ? 'always' : 'auto');
          // Story 4-116 — `heading: 'hidden'` renders NO heading at all
          // (`null`), never an empty one; every other mode resolves to real
          // text (see `sectionHeadingText`'s own comment).
          const headingText = sectionHeadingText(section, sectionIndex);
          // Story 4-116 — `repeatHeading` (table sections only): opts the
          // table INTO spanning a physical page with its `<thead>` repeated
          // on each one, overriding `.report-section`'s own unconditional
          // `break-inside: avoid` (see `reportPrint.css`'s matching rule and
          // `pdfExport.service.js`'s own comment for the real, live-verified
          // Puppeteer mechanism this class enables).
          const presentation = presentationOf(section);
          const repeatHeadingOn = section.kind === 'table' && presentation.repeatHeading;
          return (
          <Fragment key={section.key}>
            {pageBreak === 'always' && (
              // On-screen-only indicator of where a real break lands — hidden
              // under `@media print` (print.css), which is also where the
              // REAL break (`.report-page-break` below) takes effect.
              <div
                className="report-page-break-marker my-1 flex items-center gap-2 text-caption font-semibold uppercase tracking-wide text-muted-foreground"
                aria-hidden="true"
              >
                <span className="h-px flex-1 bg-border" />
                Page break
                <span className="h-px flex-1 bg-border" />
              </div>
            )}
            <section
              className={`report-section space-y-2 rounded-md transition-shadow ${
                pageBreak === 'always' ? 'report-page-break' : pageBreak === 'avoid' ? 'report-break-avoid' : ''
              } ${repeatHeadingOn ? 'report-section--repeat-heading' : ''} ${
                onSelectSection ? 'cursor-pointer hover:ring-1 hover:ring-border' : ''
              } ${
                // Story 4-113 — the ONE consistent selection treatment this
                // story's own interaction contract requires ("selection uses
                // one consistent focus treatment"), a ring in the same
                // `--ring` token `.focus-ring` (index.css) already uses for
                // keyboard focus everywhere else in this app, not a new,
                // bespoke selection color.
                selectedSectionKey === section.key ? 'ring-2 ring-ring ring-offset-2 ring-offset-background' : ''
              }`}
              // Story 4-111 — see `contracts/reports.js#ReportContentSection.tocKey`'s
              // own comment; the backend PDF twin (`renderSection`) stamps
              // the identical attribute from the identical field.
              data-toc-key={section.tocKey ?? undefined}
              // Story 4-113 — the one stable hook Report Studio's canvas (both
              // this component directly in 'web' mode, and `PagedReportPreview`'s
              // event-delegation click handler in 'print' mode, since Paged.js
              // clones this exact static markup outside React's own tree) uses
              // to turn a canvas click into a selection. `section.key` IS the
              // template section's own id for every real section (only the
              // synthetic 'overview' row is not one — see
              // `reportPreviewContent.ts`'s own comment), so this needs no
              // second id scheme.
              data-report-section-id={section.key}
              onClick={onSelectSection ? () => onSelectSection(section.key) : undefined}
            >
              {headingText && (
                <SectionHeading
                  text={headingText}
                  headerStyle={content.style.sectionHeaderStyle}
                  accent={brandColor}
                  headingScale={content.style.headingScale}
                  underlineWidth={content.style.sectionUnderlineWidth}
                  radius={content.style.surfaceRadius}
                  headingColor={headingColor}
                  borderColor={borderColor}
                />
              )}
              {section.body && <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{section.body}</p>}
              {section.kind === 'table' && section.columns && section.tableRows ? (
                <ReportDataTable
                  columns={section.columns}
                  columnKeys={section.columnKeys}
                  rows={section.tableRows}
                  tableColumns={presentation.tableColumns}
                  overflow={presentation.overflow}
                  showTotalsRow={presentation.showTotalsRow}
                  caption={section.heading}
                  borderWeight={borderDensity}
                  striped={styleVariants.table === 'striped'}
                  stripeColor={borderColor}
                />
              ) : presentation.layout === 'narrative' ? (
                <NarrativeSectionBody section={section} toggles={rowToggles} />
              ) : presentation.layout === 'summaryCards' ? (
                <SummaryCardsSectionBody section={section} toggles={rowToggles} captionScale={typographyRoles.caption} />
              ) : (
                <ListSectionBody section={section} toggles={rowToggles} captionScale={typographyRoles.caption} />
              )}
            </section>
          </Fragment>
          );
        })}

      {!mediaSummaryIsAppendix && mediaSummaryBlock}

      {toggles.flaggedItems && content.flaggedItems && (
        <ReportFindingsRollup
          variant="flagged"
          title="Flagged items"
          total={content.flaggedItems.total}
          bySeverity={content.flaggedItems.bySeverity}
          items={content.flaggedItems.items}
          radius={content.style.surfaceRadius}
          groupBy={content.findingsGroupBy}
          borderWeight={borderDensity}
          compact={styleVariants.finding === 'compact'}
          severityColor={findingSeverityColor}
        />
      )}

      {toggles.actions && content.actions && (
        <ReportFindingsRollup
          variant="actions"
          title="Recommended actions"
          total={content.actions.total}
          items={content.actions.items}
          radius={content.style.surfaceRadius}
          groupBy={content.findingsGroupBy}
          borderWeight={borderDensity}
          compact={styleVariants.finding === 'compact'}
          severityColor={findingSeverityColor}
        />
      )}

      {toggles.disclaimer && content.disclaimer && <ReportDisclaimer text={content.disclaimer} />}

      {/* Story 4-117 — see `appendixPhotosOf`'s own comment above. Rendered
          unconditionally when non-empty — the presence of >=1 section opted
          into `evidencePlacement: 'appendix'` is itself the intent signal,
          not a second toggle to also remember to turn on. */}
      {appendixPhotos.length > 0 && (
        <section className="report-section space-y-2" data-toc-key="appendix">
          <SectionHeading
            text="Supporting documents"
            headerStyle={content.style.sectionHeaderStyle}
            accent={brandColor}
            headingScale={content.style.headingScale}
            underlineWidth={content.style.sectionUnderlineWidth}
            radius={content.style.surfaceRadius}
            headingColor={headingColor}
            borderColor={borderColor}
          />
          <ReportMediaSummaryGrid
            totalPhotos={appendixPhotos.length}
            roomsWithPhotos={new Set(appendixPhotos.map((p) => p.roomName)).size}
            photos={appendixPhotos}
            thumbnailSize={content.style.thumbnailSize}
            groupNoun="section"
          />
        </section>
      )}

      {mediaSummaryIsAppendix && mediaSummaryBlock}

      {/* Story 4-128e — the trial mark, moved out of the backend's own HTML
          twin (`pdfExport.service.js`'s `<div class="watermark">TRIAL
          VERSION</div>` + its `.watermark` rule) and into the shared document.
          P06 §4.3: "Trial marks and permission-dependent content must be
          normalized into the same render input in both hosts."

          It is NOT derived here. Whether a render carries the mark is a
          BILLING fact — `report.controller.js` resolves it from the
          organisation's subscription and stores the answer on the version
          (`report.versions[].watermark`) — and a document component that asked
          that question itself would be a second, drifting authority over it.
          So the caller passes the resolved text or nothing.

          LAST child, deliberately. Paged.js's PositionFixed handler pulls a
          `position: fixed` element out of flow and clones it onto every
          physical page (the same mechanism the repeating letterhead uses), and
          placing it last keeps every `X + Y` sibling selector in
          `paginate.ts`'s generated stylesheet — which the cover-page break
          rules depend on — matching exactly what it matched before. */}
      {watermark ? (
        <div className="report-watermark" aria-hidden="true">
          {watermark}
        </div>
      ) : null}
    </article>
  );
}
